import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import NotificationCenter from "@/components/NotificationCenter";
import { 
  LayoutDashboard, 
  Users, 
  Gift, 
  Mail, 
  Settings, 
  Clock,
  ChevronLeft,
  ChevronRight,
  Shield,
  Building2,
  UserCircle2,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ChevronUp,
  Database,
  Key,
  Send,
  FileText,
  Sparkles,
  Network
} from "lucide-react";
import { useTranslation } from "react-i18next";
import ThemeToggle from "@/components/ThemeToggle";

// Navigation configuration with icons for each role
const NAV_CONFIG = {
  STAFF: [
    { to: "/staff/dashboard", label: "nav.staffDashboard", icon: Shield },
    { to: "/updates", label: "nav.updates", icon: Clock },
    { to: "/sessions", label: "nav.sessions", icon: UserCircle2 },
  ],
  SYSTEM_OWNER: [
    { to: "/system?tab=analytics", label: "nav.overview", icon: LayoutDashboard },
    { to: "/system?tab=agencies", label: "nav.agencies", icon: Building2 },
    { to: "/system?tab=staff", label: "nav.staff", icon: Users },
    { to: "/system?tab=notifications", label: "nav.notifications", icon: Mail },
    { to: "/system?tab=audit", label: "nav.auditLogs", icon: Clock },
    { to: "/system?tab=settings", label: "nav.systemSettings", icon: Database },
    { to: "/updates", label: "nav.updates", icon: Clock },
    { to: "/sessions", label: "nav.sessions", icon: UserCircle2 },
  ],
  ORG_OWNER: (sendmireinEnabled, _agencyRole, stampCardsEnabled) => [
    { to: "/merchant", label: "nav.dashboard", icon: LayoutDashboard },
    { to: "/integrations", label: "nav.integrations", icon: Network },
    ...(stampCardsEnabled ? [{ to: "/stamp-cards", label: "nav.stampCards", icon: Gift }] : []),
    ...(sendmireinEnabled ? [{ 
      to: "/sendmirein", 
      label: "nav.newsletter", 
      icon: Mail,
      badge: "New"
    }] : []),
    { to: "/employees", label: "nav.employees", icon: Users },
    { to: "/redeemer", label: "nav.redeem", icon: Gift },
    { to: "/updates", label: "nav.updates", icon: Clock },
    { to: "/sessions", label: "nav.sessions", icon: UserCircle2 },
    { to: "/security", label: "nav.security", icon: Shield },
  ],
  EMPLOYEE: [
    { to: "/merchant", label: "nav.dashboard", icon: LayoutDashboard },
    { to: "/redeemer", label: "nav.redeem", icon: Gift },
    { to: "/updates", label: "nav.updates", icon: Clock },
    { to: "/sessions", label: "nav.sessions", icon: UserCircle2 },
  ],
  AGENCY: (sendmireinEnabled, agencyRole) => [
    { to: "/agency", label: "nav.agency", icon: Building2 },
    { to: "/agency/organizations", label: "nav.agencyOrganizations", icon: Building2 },
    ...(!agencyRole || agencyRole === "OWNER" ? [{ to: "/agency/employees", label: "nav.agencyEmployees", icon: Users }] : []),
    ...(!agencyRole || agencyRole === "OWNER" ? [{ to: "/agency/billing", label: "nav.agencyBilling", icon: FileText }] : []),
    { to: "/updates", label: "nav.updates", icon: Clock },
    { to: "/sessions", label: "nav.sessions", icon: UserCircle2 },
    { to: "/security", label: "nav.security", icon: Shield },
  ],
  REDEEMER: [
    { to: "/redeemer", label: "nav.redeem", icon: Gift },
    { to: "/updates", label: "nav.updates", icon: Clock },
    { to: "/sessions", label: "nav.sessions", icon: UserCircle2 },
    { to: "/security", label: "nav.security", icon: Shield },
  ],
};

const Sidebar = () => {
  const { user, company, logout } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  
  if (!user) return null;
  
  // Hide sidebar on public pages
  const publicPaths = ['/login', '/activate', '/owner-setup', '/reset-password', '/staff/set-password'];
  if (publicPaths.includes(location.pathname)) return null;
  
  const appLogo = "/icons/Logo_New.png";
  
  // Get navigation links based on role
  const sendmireinEnabled = company?.sendmireinEnabled || false;
  const stampCardsEnabled = !!company?.stampCardsEnabled;
  const config = NAV_CONFIG[user.role];
  const links = typeof config === 'function' ? config(sendmireinEnabled, user?.agencyRole, stampCardsEnabled) : (config || []);
  
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };
  
  const toggleSubmenu = (linkTo) => {
    setExpandedMenus(prev => ({
      ...prev,
      [linkTo]: !prev[linkTo]
    }));
  };
  
  const NavLinks = () => (
    <>
      {links.map((link) => {
        const Icon = link.icon;
        const basePath = link.to.split('?')[0];
          const currentPath = location.pathname;
          const currentSearch = location.search || "";
          const isActive = (() => {
            // Exact match (including query)
            if (`${currentPath}${currentSearch}` === link.to) return true;

            // If link has no query params, match path only.
            if (!link.to.includes("?")) {
              return currentPath === basePath;
            }

            // If link has query params, require the same path AND all query params to match.
            if (currentPath !== basePath) return false;
            const [_, linkQuery] = link.to.split("?");
            const linkParams = new URLSearchParams(linkQuery || "");
            const currentParams = new URLSearchParams(currentSearch.startsWith("?") ? currentSearch.slice(1) : currentSearch);
            for (const [k, v] of linkParams.entries()) {
              if (currentParams.get(k) !== v) return false;
            }
            return true;
          })();
        const isExpanded = expandedMenus[link.to];
        const hasSubmenu = link.submenu && link.submenu.length > 0;
        
        return (
          <div key={link.to}>
            {/* Main Link */}
            <div
              className={`
                flex items-center gap-3 px-4 py-3 rounded-lg transition-all cursor-pointer
                ${isActive 
                  ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg' 
                  : 'text-foreground hover:bg-accent'
                }
                ${collapsed ? 'justify-center' : ''}
              `}
              onClick={() => {
                if (hasSubmenu && !collapsed) {
                  toggleSubmenu(link.to);
                } else {
                  navigate(link.to);
                  setMobileOpen(false);
                }
              }}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {!collapsed && (
                <>
                  <span className="font-medium flex-1">{t(link.label)}</span>
                  {link.badge && (
                    <span className="px-2 py-0.5 text-xs font-semibold bg-green-500 text-white rounded-full">
                      {link.badge}
                    </span>
                  )}
                  {hasSubmenu && (
                    isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                  )}
                </>
              )}
            </div>
            
            {/* Submenu */}
            {hasSubmenu && !collapsed && isExpanded && (
              <div className="ml-4 mt-1 space-y-1 border-l-2 border-border pl-4">
                {link.submenu.map((sublink) => {
                  const SubIcon = sublink.icon;
                  const subIsActive = location.pathname + location.search === sublink.to ||
                                     location.search.includes(sublink.to.split('?')[1]);
                  
                  return (
                    <Link
                      key={sublink.to}
                      to={sublink.to}
                      onClick={() => setMobileOpen(false)}
                      className={`
                        flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all
                        ${subIsActive 
                          ? 'bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 font-medium' 
                          : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                        }
                      `}
                    >
                      <SubIcon className="w-4 h-4 flex-shrink-0" />
                      <span>{t(sublink.label)}</span>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
  
  // Desktop Sidebar
  const DesktopSidebar = (
    <aside
      className={`
        hidden lg:flex flex-col bg-card border-r border-border 
        transition-all duration-300 h-screen sticky top-0 shadow-sm
        ${collapsed ? 'w-20' : 'w-64'}
      `}
    >
      {/* Logo & Toggle */}
      <div className="flex items-center justify-between p-4 border-b border-border bg-card">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <div className="relative">
              <img src={appLogo} alt="Scanmirein" className="w-8 h-8 rounded-lg shadow-sm" />
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-card"></div>
            </div>
            <div>
                  <span className="font-bold text-lg bg-gradient-to-r from-blue-600 to-blue-500 bg-clip-text text-transparent">
                Scanmirein
              </span>
              <div className="text-xs text-muted-foreground">{t('nav.dashboard')}</div>
            </div>
          </div>
        )}
        <div className="flex items-center gap-2">
          {!collapsed && <NotificationCenter />}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-2 hover:bg-blue-50 rounded-lg transition-all hover:scale-110"
            title={collapsed ? t('ui.expand') : t('ui.collapse')}
          >
            {collapsed ? 
              <ChevronRight className="w-5 h-5 text-blue-600" /> : 
              <ChevronLeft className="w-5 h-5 text-muted-foreground" />
            }
          </button>
        </div>
      </div>
      
      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto custom-scrollbar">
        <NavLinks />
      </nav>
      
      {/* User Info & Logout */}
      <div className="border-t border-border p-4 space-y-2 bg-card">
        {!collapsed && (
          <div className="px-4 py-3 bg-muted rounded-lg mb-2">
            <div className="flex items-center gap-2 mb-1">
              <UserCircle2 className="w-4 h-4 text-blue-600" />
              <div className="text-xs font-medium text-foreground">
                {user.role.replace('_', ' ')}
              </div>
            </div>
            <div className="text-xs text-muted-foreground truncate">
              {user.role === 'STAFF' ? company?.name : user.email}
            </div>
          </div>
        )}
        {!collapsed && <ThemeToggle className="w-full justify-start px-3" />}
        <button
          onClick={handleLogout}
          className={`
            flex items-center gap-3 w-full px-4 py-3 rounded-lg
            text-red-600 hover:bg-red-50 transition-all hover:shadow-md
            ${collapsed ? 'justify-center' : ''}
          `}
          title="Abmelden"
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          {!collapsed && <span className="font-medium">{t('nav.logout')}</span>}
        </button>
      </div>
    </aside>
  );
  
  // Mobile Top Bar + Drawer
  const MobileNav = (
    <>
      {/* Top Bar */}
      <div className="lg:hidden sticky top-0 z-50 bg-card border-b border-border px-4 py-3 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-2">
          <div className="relative">
            <img src={appLogo} alt="Scanmirein" className="w-8 h-8 rounded-lg" />
            <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-green-500 rounded-full border border-white"></div>
          </div>
          <div>
            <span className="font-bold text-base bg-gradient-to-r from-blue-600 to-blue-500 bg-clip-text text-transparent">
              Scanmirein
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <NotificationCenter />
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-2 hover:bg-accent rounded-lg transition-all"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      
      {/* Mobile Drawer Overlay */}
      {mobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
        />
      )}
      
      {/* Mobile Drawer */}
      <aside
        className={`
          lg:hidden fixed top-0 left-0 h-full w-80 bg-card z-50
          transform transition-transform duration-300 shadow-2xl
          flex flex-col
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Logo & Close */}
        <div className="flex items-center justify-between p-4 border-b border-border bg-card">
          <div className="flex items-center gap-2">
            <div className="relative">
              <img src={appLogo} alt="Scanmirein" className="w-8 h-8 rounded-lg shadow-sm" />
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-card"></div>
            </div>
            <div>
              <span className="font-bold text-lg bg-gradient-to-r from-blue-600 to-blue-500 bg-clip-text text-transparent">
              Scanmirein
            </span>
            <div className="text-xs text-muted-foreground">{t('nav.dashboard')}</div>
            </div>
          </div>
          <button
            onClick={() => setMobileOpen(false)}
            className="p-2 hover:bg-accent rounded-lg transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Navigation - scrollable */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto custom-scrollbar">
          <NavLinks />
        </nav>
        
        {/* User Info & Logout - fixed at bottom */}
        <div className="border-t border-border p-4 space-y-2 bg-card">
          <div className="px-4 py-3 bg-muted rounded-lg mb-2">
            <div className="flex items-center gap-2 mb-1">
              <UserCircle2 className="w-4 h-4 text-blue-600" />
              <div className="text-xs font-medium text-foreground">
                {user.role.replace('_', ' ')}
              </div>
            </div>
            <div className="text-xs text-muted-foreground truncate">
              {user.role === 'STAFF' ? company?.name : user.email}
            </div>
          </div>
          <ThemeToggle className="w-full justify-start px-3" />
          <button
            onClick={() => {
              setMobileOpen(false);
              handleLogout();
            }}
            className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-all"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">{t('nav.logout')}</span>
          </button>
        </div>
      </aside>
    </>
  );
  
  return (
    <>
      {/* Desktop Sidebar - only on large screens */}
      {DesktopSidebar}
      
      {/* Mobile Navigation - only on small screens */}
      {MobileNav}
    </>
  );
};

export default Sidebar;
